import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import api, { partService, truckService } from '../api/api';
import { confirmOdometerUpdate } from '../utils/odometer';
import { formatPortalDate } from '../utils/dateFormat';
import { ServiceInventoryPartOption, ServicePartThumbnail, serviceInventoryPartSearchText } from './ServiceInventoryPartOption';
import ServiceLogAttachments from './ServiceLogAttachments';
import ServiceAttentionNotice from './ServiceAttentionNotice';
import ServiceLogInventoryEditPicker from './ServiceLogInventoryEditPicker';

function toDateInputValue(d) {
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  // Use UTC fields to avoid off-by-one issues from timezone conversion for date-only DB fields.
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatOdometer(value) {
  if (value === null || value === undefined || value === '') return '-';
  const parsed = typeof value === 'number' ? value : parseInt(String(value).replace(/[^0-9-]/g, ''), 10);
  if (!Number.isFinite(parsed)) return String(value);
  return parsed.toLocaleString();
}

function ServiceRow({ row, onDelete, onEdit }) {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const serviceDate = useMemo(() => toDateInputValue(row.service_date), [row.service_date]);
  const odometerValue = row?.odometer === null || row?.odometer === undefined || row?.odometer === '' ? '' : String(row.odometer);

  const toggleDetails = async () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (!nextOpen || details || detailsLoading) return;
    try {
      setDetailsLoading(true);
      setDetailsError('');
      const response = await api.get(`/service-logs/${row.id}/details`);
      setDetails(response.data || null);
    } catch (error) {
      setDetailsError(error?.response?.data?.error || error?.message || 'Failed to load inventory parts.');
    } finally {
      setDetailsLoading(false);
    }
  };

  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={toggleDetails} aria-label="Toggle details">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 'bold' }}>{serviceDate ? formatPortalDate(serviceDate) : 'N/A'}</TableCell>
        <TableCell>{formatOdometer(odometerValue)}</TableCell>
        <TableCell>{row.service_done}</TableCell>
        <TableCell>
          {Number(row.inventory_part_count || 0) > 0 ? (
            <Chip size="small" icon={<Inventory2Icon />} label={`${Number(row.inventory_part_count)} used`} color="success" variant="outlined" sx={{ fontWeight: 800 }} />
          ) : (
            <Typography variant="caption" color="text.secondary">None recorded</Typography>
          )}
        </TableCell>
        <TableCell>{row.performed_by || '-'}</TableCell>
        <TableCell align="right">
          <IconButton size="small" onClick={() => onEdit?.(row)} aria-label="Edit entry">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(row.id)} aria-label="Delete entry">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#e65100' }}>
                      Repairs Needed
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {row.repairs_needed || '-'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#1565c0' }}>
                      Notes
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {row.notes || '-'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
              {!detailsLoading && !detailsError && details && (
                <Box sx={{ mt: 2 }}>
                  <ServiceLogAttachments
                    serviceLogId={row.id}
                    initialImages={details.repair_images || []}
                    onChange={(repairImages) => setDetails((current) => current ? { ...current, repair_images: repairImages } : current)}
                  />
                </Box>
              )}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 900, color: '#087f5b', mb: 1 }}>
                  <Inventory2Icon fontSize="small" /> Inventory Parts Used
                </Typography>
                {detailsLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}><CircularProgress size={18} /><Typography variant="body2">Loading inventory parts...</Typography></Box>
                ) : detailsError ? (
                  <Alert severity="error">{detailsError}</Alert>
                ) : !details?.used_inventory_parts?.length ? (
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}><Typography variant="body2" color="text.secondary">No inventory parts were recorded for this service.</Typography></Paper>
                ) : (
                  <Grid container spacing={1.5}>
                    {details.used_inventory_parts.map((part) => (
                      <Grid item xs={12} key={part.sku || part.installed_part_ids?.[0]}>
                        <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2, borderColor: 'rgba(8,127,91,0.25)' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                            <Box>
                              <Typography sx={{ fontWeight: 900, color: '#001f3f' }}>{part.part_type || 'Inventory Part'}</Typography>
                              <Typography variant="body2"><strong>SKU:</strong> {part.sku || '—'} &nbsp; <strong>BioMed SKU:</strong> {part.biomed_part_number_sku || '—'}</Typography>
                              <Typography variant="body2"><strong>OEM:</strong> {part.part_number_oem || '—'} &nbsp; <strong>Serial:</strong> {part.serial_number || '—'}</Typography>
                              <Typography variant="body2"><strong>Make / Model:</strong> {[part.make, part.model].filter(Boolean).join(' ') || '—'}</Typography>
                            </Box>
                            <Box><Chip size="small" label={`Quantity used: ${part.quantity}`} color="success" sx={{ fontWeight: 800 }} /></Box>
                          </Box>
                          {Array.isArray(part.photos) && part.photos.length > 0 && (
                            <Box sx={{ mt: 1.5 }}>
                              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 800, mb: 0.75 }}><PhotoLibraryIcon fontSize="small" /> Part photos</Typography>
                              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(105px, 1fr))', gap: 1 }}>
                                {part.photos.map((photo, index) => (
                                  <Box key={photo.id || photo.url || index} component="a" href={photo.url} target="_blank" rel="noreferrer" sx={{ display: 'block', aspectRatio: '4 / 3', borderRadius: 1.5, overflow: 'hidden', border: '1px solid rgba(0,31,63,0.14)' }}>
                                    <Box component="img" src={photo.url} alt={photo.name || `${part.sku} part`} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function TruckServiceLog({ truckId }) {
  const [logs, setLogs] = useState([]);
  const [repairTasks, setRepairTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addError, setAddError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editRow, setEditRow] = useState(null);
  const [editServiceDate, setEditServiceDate] = useState('');
  const [editServiceDone, setEditServiceDone] = useState('');
  const [editOdometer, setEditOdometer] = useState('');
  const [editPerformedBy, setEditPerformedBy] = useState('');
  const [editRepairsNeeded, setEditRepairsNeeded] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editConsumableIds, setEditConsumableIds] = useState([]);
  const [editUsedInventoryParts, setEditUsedInventoryParts] = useState([]);
  const [editRestoreInventoryPartIds, setEditRestoreInventoryPartIds] = useState([]);
  const [editSelectedInventoryPartIds, setEditSelectedInventoryPartIds] = useState([]);
  const [editInventoryQuantities, setEditInventoryQuantities] = useState({});
  const [editInventoryLoading, setEditInventoryLoading] = useState(false);
  const [taskSaving, setTaskSaving] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [error, setError] = useState('');
  const [serviceNotes, setServiceNotes] = useState('');
  const [truck, setTruck] = useState(null);
  const [consumables, setConsumables] = useState([]);
  const [inventoryParts, setInventoryParts] = useState([]);

  const [serviceDate, setServiceDate] = useState(() => toDateInputValue(new Date()));
  const [serviceDone, setServiceDone] = useState('');
  const [odometer, setOdometer] = useState('');
  const [performedBy, setPerformedBy] = useState('');
  const [repairsNeeded, setRepairsNeeded] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedConsumableIds, setSelectedConsumableIds] = useState([]);
  const [selectedInventoryPartIds, setSelectedInventoryPartIds] = useState([]);
  const [inventoryQuantities, setInventoryQuantities] = useState({});

  const [taskName, setTaskName] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const notesHydratedRef = useRef(false);
  const lastSavedNotesRef = useRef('');

  const load = async () => {
    if (!truckId) return;
    try {
      setLoading(true);
      const [logsRes, tasksRes, truckRes, consumablesRes, inventoryRes] = await Promise.all([
        truckService.getServiceLogs(truckId),
        truckService.getRepairTasks(truckId).catch(() => ({ data: [] })),
        truckService.getById(truckId).catch(() => ({ data: null })),
        truckService.getConsumables(truckId).catch(() => ({ data: { rows: [] } })),
        partService.getInventory().catch(() => ({ data: [] })),
      ]);
      setLogs(logsRes.data || []);
      setRepairTasks(tasksRes.data || []);
      setTruck(truckRes?.data || null);
      setOdometer((current) => current || (truckRes?.data?.odometer == null ? '' : String(truckRes.data.odometer)));
      setConsumables((consumablesRes.data?.rows || []).filter((item) => item.active !== false));
      setInventoryParts((inventoryRes.data || []).filter((part) => Number(part.quantity ?? 0) > 0 && (part.representative_id || part.id)));
      const initialNotes = truckRes?.data?.service_notes || '';
      setServiceNotes(initialNotes);
      lastSavedNotesRef.current = initialNotes;
      notesHydratedRef.current = true;
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e?.message || 'Failed to load service log.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setError('');
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truckId]);

  const inventoryPartId = (part) => Number(part.representative_id || part.id);
  const inventoryPartLabel = (part) => [
    part.part_type || part.description || 'Part',
    part.sku || part.biomed_part_number_sku || part.part_number_oem,
    part.location,
  ].filter(Boolean).join(' • ');
  const selectedInventoryParts = useMemo(() => (
    selectedInventoryPartIds
      .map((id) => inventoryParts.find((part) => Number(inventoryPartId(part)) === Number(id)))
      .filter(Boolean)
  ), [inventoryParts, selectedInventoryPartIds]);
  const editSelectedInventoryParts = useMemo(() => (
    editSelectedInventoryPartIds
      .map((id) => inventoryParts.find((part) => Number(inventoryPartId(part)) === Number(id)))
      .filter(Boolean)
  ), [editSelectedInventoryPartIds, inventoryParts]);
  const vehicleLabel = [
    truck?.name,
    truck?.license_plate,
    [truck?.year, truck?.make, truck?.model].filter(Boolean).join(' '),
  ].filter(Boolean).join(' • ') || `Vehicle #${truckId}`;

  const handleInventorySelectionChange = (value) => {
    const ids = (typeof value === 'string' ? value.split(',') : value).map((item) => Number(item));
    setSelectedInventoryPartIds(ids);
    setInventoryQuantities((prev) => {
      const next = {};
      ids.forEach((id) => {
        next[id] = prev[id] || 1;
      });
      return next;
    });
  };

  const handleEditInventorySelectionChange = (parts) => {
    const ids = parts.map((part) => Number(inventoryPartId(part)));
    setEditSelectedInventoryPartIds(ids);
    setEditInventoryQuantities((previous) => {
      const next = {};
      ids.forEach((id) => {
        next[id] = previous[id] || 1;
      });
      return next;
    });
  };

  const handleAdd = async () => {
    if (!truckId) return;
    if (!serviceDate || !serviceDone.trim()) {
      setAddError('Service Date and Service Done are required.');
      return;
    }
    if (selectedConsumableIds.length && !String(odometer || '').trim()) {
      setAddError('Odometer is required when marking scheduled maintenance reminders completed.');
      return;
    }

    try {
      setSaving(true);
      setAddError('');
      if (!await confirmOdometerUpdate({
        currentValue: truck?.odometer,
        nextValue: odometer,
        context: 'update the vehicle odometer from this service log entry',
      })) {
        setSaving(false);
        return;
      }
      await truckService.createServiceLog(truckId, {
        service_date: serviceDate,
        service_done: serviceDone.trim(),
        odometer: odometer || null,
        performed_by: performedBy || null,
        repairs_needed: repairsNeeded || null,
        notes: notes || null,
        consumable_ids: selectedConsumableIds,
        inventory_parts: selectedInventoryPartIds.map((id) => ({
          part_id: id,
          quantity: Number(inventoryQuantities[id] || 1),
        })),
      });
      setServiceDone('');
      setOdometer('');
      setPerformedBy('');
      setRepairsNeeded('');
      setNotes('');
      setSelectedConsumableIds([]);
      setSelectedInventoryPartIds([]);
      setInventoryQuantities({});
      await load();
      setAddOpen(false);
      window.dispatchEvent(new Event('dataUpdated'));
      window.dispatchEvent(new Event('partsUpdated'));
      window.dispatchEvent(new Event('vehiclesUpdated'));
    } catch (e) {
      console.error(e);
      setAddError(e?.response?.data?.error || e?.message || 'Failed to add service log entry.');
    } finally {
      setSaving(false);
    }
  };

  const closeAdd = () => {
    if (saving) return;
    setAddOpen(false);
    setAddError('');
  };

  const handleDelete = async (logId) => {
    if (!truckId) return;
    try {
      await truckService.deleteServiceLog(truckId, logId);
      setLogs((prev) => prev.filter((l) => l.id !== logId));
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e?.message || 'Failed to delete entry.');
    }
  };

  const openEdit = async (row) => {
    setEditError('');
    setEditRow(row);
    setEditServiceDate(toDateInputValue(row?.service_date));
    setEditServiceDone(row?.service_done || '');
    setEditOdometer(row?.odometer ?? '');
    setEditPerformedBy(row?.performed_by || '');
    setEditRepairsNeeded(row?.repairs_needed || '');
    setEditNotes(row?.notes || '');
    setEditConsumableIds([]);
    setEditUsedInventoryParts([]);
    setEditRestoreInventoryPartIds([]);
    setEditSelectedInventoryPartIds([]);
    setEditInventoryQuantities({});
    setEditOpen(true);
    try {
      setEditInventoryLoading(true);
      const response = await api.get(`/service-logs/${row.id}/details`);
      setEditUsedInventoryParts(Array.isArray(response.data?.used_inventory_parts) ? response.data.used_inventory_parts : []);
      setEditConsumableIds(Array.isArray(response.data?.consumable_ids) ? response.data.consumable_ids : []);
    } catch (error) {
      setEditError(error?.response?.data?.error || error?.message || 'Failed to load inventory parts used by this service.');
    } finally {
      setEditInventoryLoading(false);
    }
  };

  const closeEdit = (force = false) => {
    if (editSaving && force !== true) return;
    setEditOpen(false);
    setEditRow(null);
    setEditError('');
    setEditUsedInventoryParts([]);
    setEditConsumableIds([]);
    setEditRestoreInventoryPartIds([]);
    setEditSelectedInventoryPartIds([]);
    setEditInventoryQuantities({});
    setEditInventoryLoading(false);
  };

  const toggleRestoreInventoryPart = (installedPartIds = []) => {
    const ids = installedPartIds.map((id) => Number(id)).filter((id) => Number.isFinite(id));
    setEditRestoreInventoryPartIds((prev) => {
      const current = new Set(prev);
      const allSelected = ids.length > 0 && ids.every((id) => current.has(id));
      ids.forEach((id) => {
        if (allSelected) current.delete(id);
        else current.add(id);
      });
      return Array.from(current);
    });
  };

  const handleSaveEdit = async () => {
    if (!truckId || !editRow?.id) return;
    if (!editServiceDate || !String(editServiceDone || '').trim()) {
      setEditError('Service Date and Service Done are required.');
      return;
    }
    if (editConsumableIds.length && !String(editOdometer ?? '').trim()) {
      setEditError('Odometer is required when completing scheduled maintenance reminders.');
      return;
    }

    try {
      setEditSaving(true);
      setEditError('');
      if (!await confirmOdometerUpdate({
        currentValue: truck?.odometer,
        nextValue: editOdometer,
        context: 'update the vehicle odometer from this service log edit',
      })) {
        setEditSaving(false);
        return;
      }
      await truckService.updateServiceLog(truckId, editRow.id, {
        service_date: editServiceDate,
        service_done: String(editServiceDone || '').trim(),
        odometer: editOdometer === '' ? null : editOdometer,
        performed_by: editPerformedBy ? editPerformedBy : null,
        repairs_needed: editRepairsNeeded ? editRepairsNeeded : null,
        notes: editNotes ? editNotes : null,
        consumable_ids: editConsumableIds,
        restore_inventory_part_ids: editRestoreInventoryPartIds,
        inventory_parts: editSelectedInventoryPartIds.map((id) => ({
          part_id: id,
          quantity: Number(editInventoryQuantities[id] || 1),
        })),
      });
      await load();
      window.dispatchEvent(new Event('dataUpdated'));
      window.dispatchEvent(new Event('partsUpdated'));
      window.dispatchEvent(new Event('vehiclesUpdated'));
      closeEdit(true);
    } catch (e) {
      console.error(e);
      setEditError(e?.response?.data?.error || e?.message || 'Failed to update entry.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleAddTask = async () => {
    if (!truckId) return;
    if (!taskName.trim()) {
      setError('Task Name is required.');
      return;
    }
    try {
      setTaskSaving(true);
      setError('');
      const res = await truckService.createRepairTask(truckId, {
        task_name: taskName.trim(),
        due_date: taskDueDate || null,
      });
      setRepairTasks((prev) => [...prev, res.data].sort((a, b) => Number(!!a.completed) - Number(!!b.completed)));
      setTaskName('');
      setTaskDueDate('');
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e?.message || 'Failed to add repair task.');
    } finally {
      setTaskSaving(false);
    }
  };

  const handleToggleTask = async (task) => {
    if (!truckId) return;
    const nextCompleted = !task.completed;
    setRepairTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, completed: nextCompleted } : t)));
    try {
      await truckService.updateRepairTask(truckId, task.id, { completed: nextCompleted });
      // reload to keep stable ordering
      const tasksRes = await truckService.getRepairTasks(truckId);
      setRepairTasks(tasksRes.data || []);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e?.message || 'Failed to update task.');
      setRepairTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, completed: task.completed } : t)));
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!truckId) return;
    try {
      await truckService.deleteRepairTask(truckId, taskId);
      setRepairTasks((prev) => prev.filter((t) => t.id !== taskId));
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e?.message || 'Failed to delete task.');
    }
  };

  const handleSaveServiceNotes = async () => {
    if (!truckId) return;
    try {
      setNotesSaving(true);
      setError('');
      await truckService.update(truckId, { service_notes: serviceNotes || null });
      lastSavedNotesRef.current = serviceNotes || '';
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e?.message || 'Failed to save notes.');
    } finally {
      setNotesSaving(false);
    }
  };

  useEffect(() => {
    if (!truckId) return undefined;
    if (!notesHydratedRef.current) return undefined;
    if ((serviceNotes || '') === (lastSavedNotesRef.current || '')) return undefined;

    const t = setTimeout(() => {
      handleSaveServiceNotes();
    }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truckId, serviceNotes]);

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2, border: '2px solid #2e7d32', backgroundColor: '#e8f5e9' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 1 }}>
          <BuildIcon /> Service Log
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setAddError('');
            setAddOpen(true);
          }}
          sx={{ bgcolor: '#001f3f', fontWeight: 900, '&:hover': { bgcolor: '#003d7a' } }}
        >
          Add Service Log
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      <Paper sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)' }}>
                <TableCell sx={{ width: 50 }} />
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Odometer</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Service Done</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Inventory Parts</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Performed By</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2">Loading service log…</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="textSecondary">No service log entries yet.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((row) => (
                  <ServiceRow key={row.id} row={row} onDelete={handleDelete} onEdit={openEdit} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={addOpen} onClose={closeAdd} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900, color: '#001f3f' }}>
          Add Service Log
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 700 }}>
            Create a service entry for this vehicle, complete maintenance reminders, and optionally consume inventory parts.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Vehicle"
                value={vehicleLabel}
                InputProps={{ readOnly: true }}
                helperText="This service log will be saved to the vehicle currently open."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Service Date *"
                type="date"
                value={serviceDate}
                onChange={(e) => setServiceDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Odometer"
                type="number"
                value={odometer}
                onChange={(e) => setOdometer(e.target.value)}
                placeholder="e.g., 125450"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12}>
              <ServiceAttentionNotice truckId={truckId} />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label="Service Done *"
                value={serviceDone}
                onChange={(e) => setServiceDone(e.target.value)}
                placeholder="e.g., Changed oil filter, grease job, brake service"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Performed By"
                value={performedBy}
                onChange={(e) => setPerformedBy(e.target.value)}
                placeholder="Mechanic / Shop name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Repairs Needed (optional)"
                value={repairsNeeded}
                onChange={(e) => setRepairsNeeded(e.target.value)}
                placeholder="List any repairs needed..."
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Extra notes for this service..."
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="vehicle-service-consumables-label">Scheduled Maintenance Completed</InputLabel>
                <Select
                  labelId="vehicle-service-consumables-label"
                  multiple
                  value={selectedConsumableIds}
                  label="Scheduled Maintenance Completed"
                  onChange={(e) => setSelectedConsumableIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {selected.map((id) => {
                        const item = consumables.find((candidate) => String(candidate.id) === String(id));
                        return <Chip key={id} size="small" label={item?.name || `#${id}`} />;
                      })}
                    </Box>
                  )}
                >
                  {consumables.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      <Checkbox checked={selectedConsumableIds.some((id) => String(id) === String(item.id))} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.category}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                Completed items are linked to this service odometer so their next vehicle reminders are calculated automatically.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fbff', borderColor: '#cfe0ff' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#001f3f', mb: 1 }}>
                  Parts Used From Inventory
                </Typography>
                <Autocomplete
                  multiple
                  fullWidth
                  size="small"
                  options={inventoryParts}
                  value={selectedInventoryParts}
                  autoHighlight
                  openOnFocus
                  disableCloseOnSelect
                  getOptionLabel={(part) => inventoryPartLabel(part)}
                  isOptionEqualToValue={(option, value) => Number(inventoryPartId(option)) === Number(inventoryPartId(value))}
                  filterOptions={(options, state) => {
                    const tokens = String(state.inputValue || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
                    if (!tokens.length) return options;
                    return options.filter((part) => tokens.every((token) => serviceInventoryPartSearchText(part).includes(token)));
                  }}
                  onChange={(_event, parts) => handleInventorySelectionChange(parts.map((part) => inventoryPartId(part)))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search inventory parts used"
                      placeholder="Type SKU, OEM, serial, type, location..."
                      helperText={`${inventoryParts.length.toLocaleString()} available • ${selectedInventoryParts.length} selected`}
                    />
                  )}
                  renderOption={(props, part, { selected }) => {
                    const id = inventoryPartId(part);
                    return <ServiceInventoryPartOption optionProps={{ ...props, key: id }} part={part} selected={selected} label={inventoryPartLabel(part)} />;
                  }}
                />
                {selectedInventoryParts.length > 0 && (
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    {selectedInventoryParts.map((part) => {
                      const id = inventoryPartId(part);
                      const available = Math.max(1, Number(part.quantity ?? 1));
                      return (
                        <Grid item xs={12} md={6} key={id}>
                          <Paper variant="outlined" sx={{ p: 1.25, display: 'flex', gap: 1.25, alignItems: 'center', borderRadius: 2 }}>
                            <ServicePartThumbnail part={part} size={58} />
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label={`Quantity used: ${part.part_type || part.sku || `Part #${id}`}`}
                              value={inventoryQuantities[id] || 1}
                              inputProps={{ min: 1, max: available }}
                              helperText={`Available: ${available.toLocaleString()} • ${part.location || part.source_locations?.join?.(', ') || 'Location unavailable'}`}
                              onChange={(e) => {
                                const value = Math.max(1, Math.min(available, Number(e.target.value || 1)));
                                setInventoryQuantities((previous) => ({ ...previous, [id]: value }));
                              }}
                            />
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Saving moves selected units from Shelf, Solvent Shed, Shop, or Parts Truck inventory into this vehicle and records movement history automatically.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeAdd} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
            onClick={handleAdd}
            disabled={saving}
            sx={{ bgcolor: '#001f3f', fontWeight: 900 }}
          >
            {saving ? 'Saving...' : 'Save Service Log'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900, color: '#001f3f' }}>
          Edit Service Log
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 700 }}>
            Update service details and return any inventory parts added by mistake.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Vehicle"
                value={vehicleLabel}
                InputProps={{ readOnly: true }}
                helperText="Vehicle cannot be changed from this service-log edit."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Service Date *"
                type="date"
                value={editServiceDate}
                onChange={(e) => setEditServiceDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Odometer"
                type="number"
                value={editOdometer}
                onChange={(e) => setEditOdometer(e.target.value)}
                placeholder="e.g., 125450"
                inputProps={{ min: 0 }}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <ServiceAttentionNotice truckId={truckId} />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Service Done *"
                value={editServiceDone}
                onChange={(e) => setEditServiceDone(e.target.value)}
                placeholder="e.g., Changed oil filter, grease job, brake service"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Performed By"
                value={editPerformedBy}
                onChange={(e) => setEditPerformedBy(e.target.value)}
                placeholder="Mechanic / Shop name"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Repairs Needed (optional)"
                value={editRepairsNeeded}
                onChange={(e) => setEditRepairsNeeded(e.target.value)}
                multiline
                minRows={2}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Notes"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                multiline
                minRows={2}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="vehicle-service-edit-consumables-label">Scheduled Maintenance Completed</InputLabel>
                <Select
                  labelId="vehicle-service-edit-consumables-label"
                  multiple
                  value={editConsumableIds}
                  label="Scheduled Maintenance Completed"
                  onChange={(event) => setEditConsumableIds(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {selected.map((id) => {
                        const item = consumables.find((candidate) => String(candidate.id) === String(id));
                        return <Chip key={id} size="small" label={item?.name || `#${id}`} />;
                      })}
                    </Box>
                  )}
                >
                  {consumables.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      <Checkbox checked={editConsumableIds.some((id) => String(id) === String(item.id))} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.category}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                Saving recalculates this vehicle&apos;s next maintenance dates and odometer targets.
              </Typography>
            </Grid>
            {(editInventoryLoading || editUsedInventoryParts.length > 0) && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fbff', borderColor: '#cfe0ff' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#001f3f', mb: 0.5 }}>
                    Inventory parts used in this service
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Select any part added by mistake. Saving will return it to inventory and remove it from this service log.
                  </Typography>
                  {editInventoryLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2">Loading used parts...</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'grid', gap: 0.75 }}>
                      {editUsedInventoryParts.map((part) => {
                        const installedIds = Array.isArray(part.installed_part_ids) ? part.installed_part_ids : [];
                        const checked = installedIds.length > 0 && installedIds.every((id) => editRestoreInventoryPartIds.includes(Number(id)));
                        return (
                          <Paper
                            key={`${part.sku || part.part_type}-${installedIds.join('-')}`}
                            variant="outlined"
                            sx={{ p: 1, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: checked ? '#ecfdf5' : 'white', borderColor: checked ? '#66bb6a' : '#d8e2ef' }}
                          >
                            <Checkbox
                              checked={checked}
                              onChange={() => toggleRestoreInventoryPart(installedIds)}
                              disabled={!installedIds.length}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 900, color: '#001f3f' }}>
                                {part.sku || 'No SKU'} • {part.part_type || 'Part'} • Qty {part.quantity || installedIds.length || 1}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                OEM: {part.part_number_oem || 'N/A'} • {checked ? 'Will be returned to inventory' : 'Keep attached to this service log'}
                              </Typography>
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  )}
                </Paper>
              </Grid>
            )}
            <Grid item xs={12}>
              <ServiceLogInventoryEditPicker
                inventoryParts={inventoryParts}
                selectedParts={editSelectedInventoryParts}
                quantities={editInventoryQuantities}
                onSelectionChange={handleEditInventorySelectionChange}
                onQuantityChange={(id, value) => setEditInventoryQuantities((previous) => ({ ...previous, [id]: value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeEdit} disabled={editSaving}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={editSaving ? <CircularProgress size={18} color="inherit" /> : <EditIcon />}
            onClick={handleSaveEdit}
            disabled={editSaving}
            sx={{ bgcolor: '#001f3f', fontWeight: 900 }}
          >
            {editSaving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper sx={{ p: 2, mt: 2.5, backgroundColor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#001f3f' }}>
          Repairs Needed (Checklist)
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <TextField
              fullWidth
              label="Task Name"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              size="small"
              placeholder="e.g., Replace mirror light"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Due Date"
              type="date"
              value={taskDueDate}
              onChange={(e) => setTaskDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleAddTask}
              disabled={taskSaving}
              startIcon={taskSaving ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
              sx={{ borderRadius: 2 }}
            >
              Add
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ mt: 1.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading tasks…</Typography>
            </Box>
          ) : repairTasks.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>
              No repair tasks yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 48 }} />
                  <TableCell sx={{ fontWeight: 'bold' }}>Task Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 160 }}>Due Date</TableCell>
                  <TableCell sx={{ width: 70, fontWeight: 'bold' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {repairTasks.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Checkbox
                        checked={!!t.completed}
                        onChange={() => handleToggleTask(t)}
                        inputProps={{ 'aria-label': `Complete ${t.task_name}` }}
                      />
                    </TableCell>
                    <TableCell sx={{ textDecoration: t.completed ? 'line-through' : 'none', color: t.completed ? 'text.secondary' : 'inherit' }}>
                      {t.task_name}
                    </TableCell>
                    <TableCell>{t.due_date ? formatPortalDate(t.due_date) : '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => handleDeleteTask(t.id)} aria-label="Delete task">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Paper>

      <Paper
        sx={{
          p: 2,
          mt: 2.5,
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          backgroundColor: '#fff9c4',
          backgroundImage: 'repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0px, rgba(0,0,0,0) 22px, rgba(0,0,0,0.18) 23px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1b1b1b' }}>
            Notes
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.7)' }}>
            {notesSaving ? 'Saving…' : ((serviceNotes || '') === (lastSavedNotesRef.current || '') ? 'Saved' : 'Typing…')}
          </Typography>
        </Box>

        <TextField
          value={serviceNotes}
          onChange={(e) => setServiceNotes(e.target.value)}
          placeholder="Write notes here..."
          fullWidth
          multiline
          minRows={8}
          variant="standard"
          InputProps={{
            disableUnderline: true,
            sx: {
              mt: 1,
              px: 1,
              py: 0.5,
              backgroundColor: 'transparent',
              fontSize: '0.98rem',
              lineHeight: '23px',
              color: '#111',
            },
          }}
        />
      </Paper>
    </Paper>
  );
}
